import { TableCell } from "@/components/ui/table.jsx";
import ClassificationGridShell from "@/components/downtime-classification/classification-grid-shell.jsx";
import ClassificationStatusPill from "@/components/downtime-classification/classification-status-pill.jsx";
import { getClassificationRowKey } from "@/components/downtime-classification/downtime-classification-utils.js";
import { useClassificationGrid } from "@/hooks/useClassificationGrid.js";

/** @type {import("@/components/downtime-classification/classification-grid-shell.jsx").ClassificationColumn[]} */
const COLUMNS = [
  { key: "Vendor", label: "Vendor", vendorFilter: true },
  { key: "Message", label: "Description" },
  { key: "Classification", label: "Classification", sortable: false },
];

/**
 * Grid of existing downtime classification mappings.
 * Supports search, vendor column filter, and column sorting; defaults to Vendor asc.
 * @param {Object} props
 * @param {import("@/api/downtime-classification-api.js").MappedClassifications[]} props.rows
 * @param {boolean} props.isLoading
 * @param {string|null} props.error
 * @param {string[]|null} [props.selectedVendors] - Controlled vendor filter (`null` = all)
 * @param {(vendors: string[]|null) => void} [props.onSelectedVendorsChange]
 * @returns {JSX.Element}
 */
export default function YourMappingsTable({
  rows,
  isLoading,
  error,
  selectedVendors: controlledSelectedVendors,
  onSelectedVendorsChange,
}) {
  const {
    search,
    setSearch,
    selectedVendors,
    setSelectedVendors,
    sortColumn,
    sortDirection,
    handleSort,
    vendorOptions,
    filteredAndSortedRows,
  } = useClassificationGrid(rows, {
    initialSortColumn: "Vendor",
    initialSortDirection: "asc",
    selectedVendors: controlledSelectedVendors,
    onSelectedVendorsChange,
  });

  return (
    <ClassificationGridShell
      search={search}
      onSearchChange={setSearch}
      searchTestId='input-search-your-mappings'
      isLoading={isLoading}
      loadingMessage='Loading your mappings...'
      error={error}
      isEmpty={rows.length === 0}
      emptyMessage='No mapped downtime reasons found'
      columns={COLUMNS}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      onSort={handleSort}
      vendorOptions={vendorOptions}
      selectedVendors={selectedVendors}
      onVendorsChange={setSelectedVendors}
      filteredAndSortedRows={filteredAndSortedRows}
      getRowKey={(row) =>
        String(row.AlarmMessageLookupId ?? getClassificationRowKey(row))
      }
      renderRow={(row) => (
        <>
          <TableCell className='text-sm text-foreground font-normal py-3 px-4'>
            {row.Vendor}
          </TableCell>
          <TableCell className='text-sm text-foreground font-normal py-3 px-4'>
            {row.Message}
          </TableCell>
          <TableCell className='text-sm text-foreground font-normal py-3 px-4'>
            <ClassificationStatusPill isMech={row.IsMech} />
          </TableCell>
        </>
      )}
    />
  );
}
